import { Badge, Box, Code, Collapse, HStack, IconButton, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Text, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";

// Variable Card Component for displaying individual variables
interface VariableCardProps {
    variable: {
        name: string;
        type: string;
        description: string;
        usage_example?: string;
    };
}

const VariableCard: React.FC<VariableCardProps> = ({ variable }) => {
    return (
        <Box p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200" _hover={{ bg: "gray.100" }}>
            <HStack spacing={3} mb={2}>
                <Code colorScheme="blue" px={2} py={1} borderRadius="md" fontWeight="semibold">{`{{${variable.name}}}`}</Code>
                <Badge colorScheme="purple" px={2} py={1} borderRadius="md">{variable.type}</Badge>
            </HStack>

            <Text fontSize="sm" color="gray.700" mb={2}>
                {variable.description}
            </Text>

            {variable.usage_example && (
            <Box p={2} bg="blue.50" borderRadius="sm" border="1px solid" borderColor="blue.200">
                <Text fontSize="xs" fontWeight="semibold" color="blue.700" mb={1}>
                    Usage Example:
                </Text>
                <Code fontSize="xs" colorScheme="blue" p={2} borderRadius="sm" bg="white" border="1px solid" borderColor="blue.100">
                    {variable.usage_example}
                </Code>
            </Box>
            )}
        </Box>
    );
};

// Variable Group Component for displaying grouped variables
interface VariableGroupProps {
    groupName: string;
    variables: Array<{
        name: string;
        type: string;
        description: string;
        usage_example?: string;
    }>;
}

const VariableGroup: React.FC<VariableGroupProps> = ({ groupName, variables }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
            <Box
                p={4}
                bg="gray.50"
                cursor="pointer"
                onClick={() => setIsExpanded(!isExpanded)}
                _hover={{ bg: "gray.100" }}
            >
                <HStack spacing={3} justify="space-between">
                    <HStack spacing={3}>
                        <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={0}>
                            {groupName.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                        </Text>
                        <Badge colorScheme="gray" px={2} py={1} borderRadius="md">
                            {variables.length} variables
                        </Badge>
                    </HStack>
                    <IconButton
                        aria-label={isExpanded ? "Collapse group" : "Expand group"}
                        icon={isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                        size="sm"
                        variant="ghost"
                        colorScheme="gray"
                    />
                </HStack>
            </Box>

            <Collapse in={isExpanded}>
                <Box p={4} bg="white">
                    <Box
                        display="grid"
                        gridTemplateColumns="repeat(auto-fit, minmax(350px, 1fr))"
                        gap={3}
                    >
                        {variables.map((variable) => (
                            <VariableCard key={variable.name} variable={variable} />
                        ))}
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

interface DocModalProps {
    isDocsOpen: boolean;
    onDocsClose: () => void;
    isLoadingDoc: boolean;
    availableVariables: Record<string, any[]>;
}

const DocModal: React.FC<DocModalProps> = ({isDocsOpen, onDocsClose, isLoadingDoc, availableVariables}) => {
    return (
        <Modal isOpen={isDocsOpen} onClose={onDocsClose} size="6xl">
            <ModalOverlay />
            <ModalContent className="eva-modal-content">
            <ModalHeader>Available Variables Documentation</ModalHeader>
            <ModalCloseButton />
            <ModalBody className="eva-modal-body eva-small-scrollbar">
                <VStack spacing={4} align="stretch">
                <Text color="gray.600">
                    Use these variables in your Jinja templates to create dynamic system prompts:
                </Text>

                {isLoadingDoc ? (
                    <Box p={4} textAlign="center">
                    <Text color="gray.500" mb={0}>Loading documentation...</Text>
                    </Box>
                ) : Object.keys(availableVariables).length === 0 ? (
                    <Box p={4} textAlign="center">
                    <Text color="gray.500" mb={0}>No variables available. Please try again later.</Text>
                    </Box>
                ) : (
                    Object.entries(availableVariables).map(([groupName, variables]) => (
                        <VariableGroup key={groupName} groupName={groupName} variables={variables} />
                    ))
                )}

                <Box p={4} bg="purple.50" borderRadius="md" mt={4} border="1px solid" borderColor="purple.200">
                    <Text fontSize="sm" fontWeight="semibold" color="purple.800" mb={2}>
                    💡 Quick Tips:
                    </Text>
                    <VStack spacing={2} align="stretch">
                    <Text fontSize="xs" color="purple.700">
                        • Use <Code colorScheme="purple">{"{{variable_name}}"}</Code> for simple variables
                    </Text>
                    <Text fontSize="xs" color="purple.700">
                        • Use <Code colorScheme="purple">{"{% for item in list_variable %}{{item}}{% endfor %}"}</Code> for lists
                    </Text>
                    </VStack>
                </Box>
                </VStack>
            </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default DocModal;
